// Track 3.3 — Voice coaching.
//
// One Voice instance per session. Each completed proactive observation is
// `speak()`ed; the instance picks an output path based on availability:
//
//   - ElevenLabs (preferred): the aero-ai service proxies the request so the
//     API key stays server-side. The server streams MP3 chunks over a
//     dedicated WS; we accumulate them into a Blob and play once the server
//     sends `audio_done`. Latency ~1-2s for one sentence — acceptable for
//     coaching beats. Switching to MediaSource for true streaming playback
//     is a future optimization.
//   - SpeechSynthesis (fallback): the platform-provided TTS. Always works,
//     much worse voice quality. Used when ELEVENLABS_API_KEY is absent in
//     the server env or quota is exhausted.
//
// Voice selection:
//   - Public (mode=default): English voice, Flash v2.5
//   - Rapha (mode=rapha_event): EN or KO voice per session locale, Turbo v3
//
// Concurrency: speak() requests overlap if the rider triggers events
// quickly. We serialize playback per Voice instance so beats land one at a
// time; older queued beats are dropped if more than 2 are pending — fresher
// is more relevant than completeness.

export type VoiceMode = 'default' | 'rapha_event';

// Wave 20.49l — categories for the per-category cooldown. Every
// audio-emitting call site tags its speak with the intent so we
// can throttle the same *kind* of message without wiping out
// unrelated ones. Untagged speaks share the 'other' bucket.
export type VoiceCategory =
  | 'baseline'
  | 'drag_tip'
  | 'position_change'
  | 'coach_beat'
  | 'interrupt'
  | 'preflight'
  | 'other';

interface SpeakOptions {
  text: string;
  locale?: string;
  mode?: VoiceMode;
  /** Caller's intent — drives quota policy server-side. */
  trigger?: string;
  /** Wave 20.49l — dedupe bucket. Same category cannot re-fire
   *  within CATEGORY_MIN_GAP_MS. */
  category?: VoiceCategory;
}

interface VoiceOptions {
  wsUrl: string;
  userId?: string;
  /** When false, all speak() calls are no-ops. Toggled from a UI button so
   *  riders who don't want voice get a silent session immediately. */
  enabled?: boolean;
}

const MAX_QUEUE = 2;
const SPEECH_SYNTHESIS_LANG: Record<string, string> = {
  en: 'en-US',
  ko: 'ko-KR',
};

// Wave 20.49l — dedupe rules enforced at the single Voice chokepoint
// so every audio path (Preflight, SessionNarrator, proactive coach,
// keyframe coach) inherits the same protection against "same
// sentence repeated immediately." Prior state had ad-hoc dedupe
// per caller — SessionNarrator had none at all.
const EXACT_TEXT_DEDUPE_MS = 20_000;
const GLOBAL_MIN_GAP_MS = 4_000;
const CATEGORY_MIN_GAP_MS = 15_000;

function normalizeText(t: string): string {
  return t.trim().toLowerCase().replace(/\s+/g, ' ');
}

export class Voice {
  private wsUrl: string;
  private userId: string;
  private enabled: boolean;
  private queue: SpeakOptions[] = [];
  private playing = false;
  private currentAudio: HTMLAudioElement | null = null;
  // Wave 20.49l — dedupe state shared ACROSS all Voice instances
  // in this tab. SessionNarrator + AeroAiPanel each create their
  // own Voice; without sharing, a coach beat and a narrator line
  // that happen to say the same sentence would both play. Timestamps
  // are Date.now() of the last successful ENQUEUE (not playback end)
  // — a rejected speak still bumps nothing.
  private static lastTextAt = new Map<string, number>();
  private static lastCategoryAt = new Map<VoiceCategory, number>();
  private static lastAnyAt = 0;

  constructor(opts: VoiceOptions) {
    this.wsUrl = opts.wsUrl;
    this.userId = opts.userId ?? '';
    this.enabled = opts.enabled ?? true;
  }

  setEnabled(v: boolean): void {
    this.enabled = v;
    if (!v) this.stop();
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  /** Drop everything pending. The currently-playing clip finishes naturally
   *  to avoid an audible cutoff mid-syllable. */
  cancelPending(): void {
    this.queue = [];
  }

  /** Hard stop — cancel queue AND cut current playback. Used when the user
   *  toggles voice off, ends the session, or unmounts the component. */
  stop(): void {
    this.queue = [];
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.src = '';
      this.currentAudio = null;
    }
    if (typeof speechSynthesis !== 'undefined') {
      speechSynthesis.cancel();
    }
    this.playing = false;
  }

  async speak(opts: SpeakOptions): Promise<void> {
    if (!this.enabled) return;
    const text = opts.text.trim();
    if (!text) return;

    // Wave 20.49l — three dedupe gates, all shared across Voice
    // instances in this tab. Any one drops the speak.
    const now = Date.now();
    const norm = normalizeText(text);
    const lastTextAt = Voice.lastTextAt.get(norm) ?? 0;
    if (now - lastTextAt < EXACT_TEXT_DEDUPE_MS) return;
    if (now - Voice.lastAnyAt < GLOBAL_MIN_GAP_MS) return;
    const category = opts.category ?? 'other';
    const lastCatAt = Voice.lastCategoryAt.get(category) ?? 0;
    // The 'interrupt' category bypasses per-category throttling
    // (it's meant to preempt), but still respects text + global.
    if (category !== 'interrupt' && now - lastCatAt < CATEGORY_MIN_GAP_MS) return;

    Voice.lastTextAt.set(norm, now);
    Voice.lastCategoryAt.set(category, now);
    Voice.lastAnyAt = now;

    // Cap the text-dedupe map so a very long session doesn't grow
    // it unbounded. Prune anything older than 2× the dedupe window.
    if (Voice.lastTextAt.size > 200) {
      const cutoff = now - EXACT_TEXT_DEDUPE_MS * 2;
      Voice.lastTextAt.forEach((t, k) => {
        if (t < cutoff) Voice.lastTextAt.delete(k);
      });
    }

    // Bounded queue — drop stalest if we're already behind, freshness > completeness.
    if (this.queue.length >= MAX_QUEUE) {
      this.queue.shift();
    }
    this.queue.push(opts);
    if (!this.playing) {
      void this.drain();
    }
  }

  private async drain(): Promise<void> {
    this.playing = true;
    try {
      while (this.queue.length > 0 && this.enabled) {
        const next = this.queue.shift()!;
        try {
          await this.playOne(next);
        } catch (err) {
          console.warn('Voice playOne failed; falling back to SpeechSynthesis', err);
          await this.playLocal(next);
        }
      }
    } finally {
      this.playing = false;
    }
  }

  private async playOne(opts: SpeakOptions): Promise<void> {
    // Try ElevenLabs via the aero-ai proxy first. If the server signals
    // unavailable (no API key, quota burst, ElevenLabs outage), fall back.
    const blob = await this.requestServerTts(opts);
    if (!blob) {
      await this.playLocal(opts);
      return;
    }
    await this.playBlob(blob);
  }

  private requestServerTts(opts: SpeakOptions): Promise<Blob | null> {
    return new Promise((resolve) => {
      let settled = false;
      let chunks: Uint8Array[] = [];

      let ws: WebSocket;
      try {
        ws = new WebSocket(this.wsUrl);
      } catch (err) {
        resolve(null);
        return;
      }

      const finish = (value: Blob | null) => {
        if (settled) return;
        settled = true;
        try {
          ws.close();
        } catch {}
        resolve(value);
      };

      // Hard ceiling — the server should respond in ~1-2s for one sentence;
      // 6s gives generous slack on slow networks before falling back.
      const timer = setTimeout(() => finish(null), 6_000);

      ws.onopen = () => {
        ws.send(
          JSON.stringify({
            type: 'tts',
            text: opts.text,
            mode: opts.mode ?? 'default',
            locale: opts.locale,
            user_id: this.userId,
            trigger: opts.trigger,
          }),
        );
      };

      ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          if (msg.type === 'audio') {
            const b = atob(msg.chunk_b64 as string);
            const bytes = new Uint8Array(b.length);
            for (let i = 0; i < b.length; i++) bytes[i] = b.charCodeAt(i);
            chunks.push(bytes);
          } else if (msg.type === 'audio_done') {
            clearTimeout(timer);
            if (chunks.length === 0) {
              finish(null);
              return;
            }
            const blob = new Blob(chunks as BlobPart[], { type: 'audio/mpeg' });
            chunks = [];
            finish(blob);
          } else if (msg.type === 'audio_unavailable') {
            clearTimeout(timer);
            finish(null);
          }
        } catch (err) {
          console.warn('Voice WS parse error', err);
        }
      };

      ws.onerror = () => {
        clearTimeout(timer);
        finish(null);
      };
      ws.onclose = () => {
        clearTimeout(timer);
        if (!settled) finish(null);
      };
    });
  }

  private playBlob(blob: Blob): Promise<void> {
    return new Promise((resolve) => {
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      this.currentAudio = audio;
      const cleanup = () => {
        URL.revokeObjectURL(url);
        if (this.currentAudio === audio) this.currentAudio = null;
        resolve();
      };
      audio.onended = cleanup;
      audio.onerror = cleanup;
      audio.play().catch(() => {
        cleanup();
      });
    });
  }

  private playLocal(opts: SpeakOptions): Promise<void> {
    return new Promise((resolve) => {
      if (typeof speechSynthesis === 'undefined') {
        resolve();
        return;
      }
      const utter = new SpeechSynthesisUtterance(opts.text);
      const localeKey = (opts.locale || 'en').slice(0, 2);
      utter.lang = SPEECH_SYNTHESIS_LANG[localeKey] || 'en-US';
      utter.rate = 1.0;
      utter.pitch = 1.0;
      utter.onend = () => resolve();
      utter.onerror = () => resolve();
      speechSynthesis.speak(utter);
    });
  }
}
